import { subscribePullRequestInvalidation } from "../pullRequestInvalidation";
import { useCallback, useState, useEffect } from "react";
import type { PullRequestSurfaceDescriptor } from "../pullRequestSurfaceStore";
import { useEnvironmentConnectionState, useEnvironments } from "../state/environments";
import { useVisibleLiveRefresh } from "./useVisibleLiveRefresh";

const PULL_REQUEST_RENDERER_SESSION_EPOCH = String(performance.timeOrigin);
let pullRequestAuthoritySequence = 0;

function nextPullRequestAuthoritySequence(): number {
  pullRequestAuthoritySequence += 1;
  return pullRequestAuthoritySequence;
}

export function usePullRequestLiveRead(
  descriptor: PullRequestSurfaceDescriptor,
  options: { readonly active: boolean; readonly partition: "summary" | "timeline" | "code" },
) {
  const { environments } = useEnvironments();
  const connectionState = useEnvironmentConnectionState(descriptor.environmentId);
  const [authoritySequence, setAuthoritySequence] = useState(nextPullRequestAuthoritySequence);
  const environment = environments.find(
    (candidate) => candidate.environmentId === descriptor.environmentId,
  );
  const connectionReady = environment?.connection.phase === "ready";
  const capabilitySupported =
    options.partition === "summary"
      ? environment?.serverConfig?.environment.capabilities.pullRequestSummary === "summary-v1"
      : options.partition === "timeline"
        ? environment?.serverConfig?.environment.capabilities.pullRequestTimeline === "timeline-v1"
        : environment?.serverConfig?.environment.capabilities.pullRequestCode === "code-v1";
  const connectionGeneration = connectionState.data?.generation;
  const ready = connectionReady && capabilitySupported && connectionGeneration !== undefined;
  const refresh = useCallback(() => setAuthoritySequence(nextPullRequestAuthoritySequence()), []);
  useEffect(
    () =>
      subscribePullRequestInvalidation((target) => {
        if (
          (!target.partitions || target.partitions.includes(options.partition)) &&
          target.environmentId === descriptor.environmentId &&
          target.projectId === descriptor.projectId &&
          target.repository === descriptor.repository &&
          target.number === descriptor.number
        )
          refresh();
      }),
    [
      options.partition,
      descriptor.environmentId,
      descriptor.projectId,
      descriptor.repository,
      descriptor.number,
      refresh,
    ],
  );
  useVisibleLiveRefresh(refresh, {
    enabled: ready && options.active && options.partition !== "code",
  });
  const requestSequence =
    options.partition === "summary"
      ? authoritySequence
      : `${options.partition}-${authoritySequence}`;
  return {
    connectionReady,
    capabilitySupported,
    connectionGeneration,
    refresh,
    authorityEpoch: ready
      ? `${PULL_REQUEST_RENDERER_SESSION_EPOCH}:${connectionGeneration}:${requestSequence}`
      : null,
  } as const;
}
